import { Alert, ControlLabel, FormControl, FormGroup } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../../../globals';
import { Link, SupportLink } from '../../../../../generic';
import { icons, IconText } from '../../../../../generic/Icon';
import Header from '../../../../../shared/settings/SettingsPageHeader';
import { useAuthenticationSettingsContext } from '../../hooks';
import { SSO_DOCS_URL } from '../consts';

import UserRoleManagement from './UserRoleManagement';

/**
 * The SAML Auth settings sub-page.
 */
const SAMLAuth: VoidFunctionComponent = () => {
  const { settings, onUpdateSAML } = useAuthenticationSettingsContext();

  if (!settings.sso?.saml) {
    return (
      <Header heading="Single Sign On with SAML">
        <Header.Paragraph>
          This feature is not currently enabled for your organization. Please contact <SupportLink>support</SupportLink>{' '}
          for more information.
        </Header.Paragraph>
      </Header>
    );
  }

  const samlSettings = settings.saml;
  if (!samlSettings) {
    // If the "saml" property isn't set, then the user's permissions changed during the current session and the user
    // wasn't given the SAML settings on the Organization object that was fetched.
    return <Alert bsStyle="info">You are not permitted to edit authentication settings.</Alert>;
  }
  return (
    <div className="gray-rounded-box margin-bottom-md sso-settings">
      <div className="margin-bottom-lg">
        <Header heading="Single Sign On with SAML">
          <Header.NavigationButton
            to={SSO_DOCS_URL}
            size="small"
            type="secondary"
          >
            <IconText icon={icons.book} text="View Documentation" />
          </Header.NavigationButton>
        </Header>
      </div>
      <p>
        Setting up single sign on with SAML will allow users with an account on your identity provider, such as Active
        Directory or Okta, to access FOSSA without having to create or manage a separate FOSSA account.
      </p>
      {!settings.subdomain && (
        <Alert>
          <span>
            Users can log in to FOSSA using this link:{' '}
            <Link to={samlSettings.loginUrl}>{samlSettings.loginUrl}</Link>
          </span>
          {window.customerDomain && (
            <div className="margin-top-xs">
              <small>You can configure a memorable link in the Login Subdomain setting above.</small>
            </div>
          )}
        </Alert>
      )}
      <form>
        <div className="padding-top-lg">
          <FormGroup>
            <ControlLabel>Callback URL</ControlLabel>
            <FormControl type="text" value={samlSettings.callbackUrl} readOnly />
          </FormGroup>
          <FormGroup>
            <ControlLabel>Audience URI / SP Entity ID</ControlLabel>
            <FormControl type="text" value={samlSettings.audienceUrl} readOnly />
          </FormGroup>
        </div>
        <div style={{ paddingTop: '30px' }}>
          <FormGroup>
            <ControlLabel>Identity Provider Single Sign On URL</ControlLabel>
            <FormControl
              type="text"
              value={samlSettings.entryPoint || ''}
              onChange={(event) => onUpdateSAML({ entryPoint: (event.target as HTMLTextAreaElement).value })}
            />
          </FormGroup>
          <FormGroup>
            <ControlLabel>Certificate</ControlLabel>
            <FormControl
              componentClass="textarea"
              style={{ height: '172px' }}
              value={samlSettings.cert || ''}
              onChange={(event) => onUpdateSAML({ cert: (event.target as HTMLTextAreaElement).value })}
            />
          </FormGroup>
        </div>
      </form>
      <UserRoleManagement />
    </div>
  );
};

export default SAMLAuth;
